import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api/auth';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=email, 2=code, 3=new password, 4=success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [devCode, setDevCode] = useState(''); // En dev, on affiche le code

  const [formData, setFormData] = useState({
    email: '',
    code: '',
    new_password: '',
    new_password_confirmation: '',
  });

  // ============ ÉTAPE 1 : Envoyer le code ============
  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await api.post('/forgot-password', { email: formData.email });
      setDevCode(res.data?.code || '');
      setStep(2);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        (typeof err.response?.data?.errors === 'object'
          ? Object.values(err.response.data.errors).flat().join(', ')
          : 'Erreur');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ============ ÉTAPE 2 : Vérifier le code ============
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.post('/verify-reset-code', {
        email: formData.email,
        code: formData.code,
      });
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Code invalide');
    } finally {
      setLoading(false);
    }
  };

  // ============ ÉTAPE 3 : Nouveau mot de passe ============
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.new_password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }

    if (formData.new_password !== formData.new_password_confirmation) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }

    setLoading(true);

    try {
      await api.post('/reset-password', {
        email: formData.email,
        code: formData.code,
        new_password: formData.new_password,
        new_password_confirmation: formData.new_password_confirmation,
      });
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  // ============ RENDU ============
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-orange-50 to-orange-100 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md">
        <div className="text-center mb-6">
          <div className="text-5xl mb-3">🔐</div>
          <h2 className="text-2xl font-bold text-[#2C3E50]">
            Mot de passe oublié
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            {step === 1 && 'Entrez votre email pour recevoir un code.'}
            {step === 2 && 'Entrez le code reçu par email.'}
            {step === 3 && 'Choisissez un nouveau mot de passe.'}
            {step === 4 && 'Mot de passe réinitialisé !'}
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-6 text-xs">
          {[1, 2, 3, 4].map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center font-bold ${
                    step >= s ? 'bg-[#E67E22] text-white' : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step > s ? '✓' : s}
                </div>
              </div>
              {i < 3 && (
                <div
                  className={`h-0.5 flex-1 mx-1 ${
                    step > s ? 'bg-[#E67E22]' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
            ❌ {error}
          </div>
        )}

        {/* ============ ÉTAPE 1 : Email ============ */}
        {step === 1 && (
          <form onSubmit={handleSendCode} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                placeholder="exemple@lycee.sn"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#E67E22] hover:bg-[#D35400] text-white py-3 rounded-lg font-semibold transition disabled:opacity-50"
            >
              {loading ? 'Envoi...' : '📧 Envoyer le code'}
            </button>
            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-gray-500 hover:text-[#E67E22]"
              >
                ← Retour à la connexion
              </Link>
            </div>
          </form>
        )}

        {/* ============ ÉTAPE 2 : Code ============ */}
        {step === 2 && (
          <form onSubmit={handleVerifyCode} className="space-y-4">
            {/* Code en dev */}
            {devCode && (
              <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 px-4 py-3 rounded-lg text-sm">
                🔧 <strong>Mode DEV</strong> : Votre code est{' '}
                <span className="font-mono font-bold text-lg">{devCode}</span>
                <br />
                <span className="text-xs">(En production, ce code sera envoyé par email)</span>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code à 6 chiffres
              </label>
              <input
                type="text"
                required
                maxLength={6}
                value={formData.code}
                onChange={(e) =>
                  setFormData({ ...formData, code: e.target.value.replace(/\D/g, '') })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22] text-center text-2xl font-mono tracking-widest"
                placeholder="000000"
              />
            </div>
            <button
              type="submit"
              disabled={loading || formData.code.length !== 6}
              className="w-full bg-[#E67E22] hover:bg-[#D35400] text-white py-3 rounded-lg font-semibold transition disabled:opacity-50"
            >
              {loading ? 'Vérification...' : '✅ Vérifier le code'}
            </button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setFormData({ ...formData, code: '' });
                }}
                className="text-sm text-gray-500 hover:text-[#E67E22]"
              >
                ← Changer d'email
              </button>
            </div>
          </form>
        )}

        {/* ============ ÉTAPE 3 : Nouveau mot de passe ============ */}
        {step === 3 && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nouveau mot de passe
              </label>
              <input
                type="password"
                required
                minLength={6}
                value={formData.new_password}
                onChange={(e) =>
                  setFormData({ ...formData, new_password: e.target.value })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                placeholder="Au moins 6 caractères"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirmer le mot de passe
              </label>
              <input
                type="password"
                required
                minLength={6}
                value={formData.new_password_confirmation}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    new_password_confirmation: e.target.value,
                  })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#E67E22] hover:bg-[#D35400] text-white py-3 rounded-lg font-semibold transition disabled:opacity-50"
            >
              {loading ? 'Modification...' : '🔐 Modifier mon mot de passe'}
            </button>
          </form>
        )}

        {/* ============ ÉTAPE 4 : Succès ============ */}
        {step === 4 && (
          <div className="text-center space-y-4">
            <div className="bg-green-50 border border-green-300 text-green-800 px-4 py-4 rounded-lg">
              ✅ Mot de passe réinitialisé avec succès !
              <p className="text-sm mt-2">
                Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
              </p>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-[#E67E22] hover:bg-[#D35400] text-white py-3 rounded-lg font-semibold transition"
            >
              🔐 Se connecter
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;